import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Icon from '@mui/material/Icon'
import Typography from '@mui/material/Typography'
import { omit } from 'ramda'
import React from 'react'

import Button from '@/app/component/atom/button'
import IconButton from '@/app/component/atom/icon-button'
import TabHeader from '@/app/component/atom/tab-header'
import Loading from '@/app/component/guard/loading'
import { useCreateHook } from '@/app/module/phonenumbers/hooks/use-create-hook'
import { useGetPhoneNumber } from '@/app/module/phonenumbers/hooks/use-get-phonenumber'
import { useUpdateHook } from '@/app/module/phonenumbers/hooks/use-update-hook'
import { ChannelType, PhoneNumber } from '@/app/module/phonenumbers/types'

import AddRuleDialog from './add-rule-dialog'
import DeleteHookDialog from './delete-hook-dialog'
import Hook, { HookProps } from './hook'
import { FooterContainer, Root, TabDivider } from './phone-number-tab-components'

type Props = Omit<HookProps, 'expanded' | 'hook' | 'index' | 'phoneNumber' | 'type' | 'saveHook' | 'setDeleteId'> & {
  phoneNumberId: number
}

const VoiceHookTab: React.FC<Props> = ({ phoneNumberId, ...props }) => {
  const [createHook, { isLoading: isCreating }] = useCreateHook(ChannelType.Voice)
  const [updateHook, { isLoading: isUpdating }] = useUpdateHook(ChannelType.Voice)
  const { data, refetch } = useGetPhoneNumber(phoneNumberId)
  const phoneNumber = data as PhoneNumber | undefined
  const hooks = React.useMemo(() => {
    if (!phoneNumber || !phoneNumber.callHooks) {
      return []
    }
    return [...phoneNumber.callHooks].sort((a, b) => a.id - b.id)
  }, [phoneNumber])

  const [expanded, setExpanded] = React.useState<number>(hooks.length ? hooks[0].id : 0)
  const [addRuleOpen, setAddRuleOpen] = React.useState(false)
  const [deleteId, setDeleteId] = React.useState<number | undefined>()

  React.useEffect(
    () =>
      setExpanded((s) => {
        if (!hooks.length) {
          return 0
        }
        if (!s || !hooks.find((h) => h.id === s)) {
          return hooks[0].id
        }
        return s
      }),
    [hooks],
  )

  const openAddRuleDialog = () => setAddRuleOpen(true)
  const closeAddRuleDialog = () => setAddRuleOpen(false)
  const closeDeleteDialog = () => setDeleteId(undefined)

  return (
    <Loading isLoading={isCreating || isUpdating}>
      <TabHeader
        title="Incoming Voice Rules"
        action={
          <IconButton aria-label="Reload Rules" tooltip="Reload Rules" onClick={refetch}>
            <Icon>refresh</Icon>
          </IconButton>
        }
      />
      <CardContent>
        <div
          style={{
            margin: 'auto',
          }}
        >
          {!!phoneNumber && (
            <Root>
              {!hooks.length && (
                <Card>
                  <CardContent>
                    <Typography component="div" color="textSecondary" variant="h6">
                      Add rules here to let us know what to do with incoming voice calls. For example,
                      <ul
                        style={{
                          marginTop: '5px',
                        }}
                      >
                        <li>configure conditions to subscribe callers to one of your campaigns</li>
                        <li>configure a webhook to be notified about every incoming voice call</li>
                        <li>configure which incoming voice calls should trigger an SMS notification to you</li>
                      </ul>
                    </Typography>
                  </CardContent>
                </Card>
              )}
              {hooks.map((hook, i) => {
                const hookId = hook.id
                return (
                  <Hook
                    {...props}
                    expanded={expanded}
                    hook={hook}
                    index={i}
                    key={hookId}
                    phoneNumber={phoneNumber}
                    type={ChannelType.Voice}
                    onChange={setExpanded}
                    saveHook={(updatedHook) => updateHook(phoneNumberId, hookId, updatedHook)}
                    setDeleteId={setDeleteId}
                  />
                )
              })}
            </Root>
          )}
          <TabDivider />
          {addRuleOpen && !!phoneNumber && (
            <AddRuleDialog
              {...props}
              expanded={expanded}
              index={-1}
              phoneNumber={phoneNumber}
              type={ChannelType.Voice}
              onClose={closeAddRuleDialog}
              saveHook={async (newHook) => {
                const created = await createHook(phoneNumberId, omit(['id'], newHook))
                if (created) {
                  closeAddRuleDialog()
                  setExpanded(created)
                }
              }}
              setDeleteId={setDeleteId}
            />
          )}

          <FooterContainer>
            <Button startIcon={<Icon>add</Icon>} variant="contained" color="primary" onClick={openAddRuleDialog}>
              Add a new Rule
            </Button>
          </FooterContainer>
        </div>
      </CardContent>

      {!!deleteId && (
        <DeleteHookDialog
          deleteId={deleteId}
          didId={phoneNumberId}
          type={ChannelType.Voice}
          onClose={closeDeleteDialog}
        />
      )}
    </Loading>
  )
}

export default VoiceHookTab
